import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchWithTimeout } from "../../App";
import "../../App.css";

const API_URL = process.env.REACT_APP_API_URL;

const RECIPE_UNITS = ["g", "kg", "ml", "L", "tsp", "tbsp", "cups", "pcs", "bags", "bottles", "cloves", "pinch"];

function RecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [recipe, setRecipe] = useState(null);
  const [allIngredients, setAllIngredients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState("");
  const [loading, setLoading] = useState(true);

  const [addIngId, setAddIngId] = useState("");
  const [addAmount, setAddAmount] = useState("");
  const [addUnit, setAddUnit] = useState("g");
  const [addError, setAddError] = useState("");

  const [editingRecipe, setEditingRecipe] = useState(false);
  const [editName, setEditName] = useState("");
  const [editPortions, setEditPortions] = useState(1);
  const [editMenuItemId, setEditMenuItemId] = useState("");
  const [menu, setMenu] = useState([]);

  const [editingIngId, setEditingIngId] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [editUnit, setEditUnit] = useState("g");

  const [sessionPrices, setSessionPrices] = useState({});
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [editPrice, setEditPrice] = useState("");
  const [editPriceQty, setEditPriceQty] = useState("");

  const fetchRecipe = async () => {
    try {
      const url = `${API_URL}/api/admin/recipes/${id}` +
        (selectedSession ? `?session_id=${selectedSession}` : "");
      const res = await fetchWithTimeout(url, { credentials: "include" });
      const data = await res.json();
      setRecipe(data);
    } catch (err) { console.error("Error fetching recipe:", err); }
    setLoading(false);
  };

  const fetchAllIngredients = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/api/admin/ingredients`, { credentials: "include" });
      const data = await res.json();
      if (Array.isArray(data)) setAllIngredients(data);
    } catch (err) { console.error("Error fetching ingredients:", err); }
  };

  const fetchMenu = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/menu`);
      const data = await res.json();
      if (Array.isArray(data)) setMenu(data);
    } catch (err) { console.error("Error fetching menu:", err); }
  };

  const fetchSessions = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/api/admin/sessions`, { credentials: "include" });
      const data = await res.json();
      if (Array.isArray(data)) setSessions(data);
    } catch (err) { console.error("Error fetching sessions:", err); }
  };

  const fetchSessionPrices = async () => {
    if (!selectedSession) return setSessionPrices({});
    try {
      const res = await fetchWithTimeout(`${API_URL}/api/admin/sessions/${selectedSession}/prices`, {
        credentials: "include"
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        const map = {};
        data.forEach(p => { map[p.ingredient_id] = p; });
        setSessionPrices(map);
      }
    } catch (err) { console.error("Error fetching session prices:", err); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { Promise.allSettled([fetchAllIngredients(), fetchSessions(), fetchMenu()]); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { Promise.allSettled([fetchRecipe(), fetchSessionPrices()]); }, [id, selectedSession]);

  const addIngredient = async (e) => {
    e.preventDefault();
    setAddError("");
    if (!addIngId) return setAddError("Please select an ingredient.");
    if (!addAmount || addAmount <= 0) return setAddError("Amount must be greater than 0.");

    const res = await fetch(`${API_URL}/api/admin/recipes/${id}/ingredients`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ ingredient_id: addIngId, amount: parseFloat(addAmount), unit: addUnit }),
    });
    const data = await res.json();
    if (!res.ok) return setAddError(data.error);

    setAddIngId(""); setAddAmount(""); setAddUnit("g");
    fetchRecipe();
  };

  const removeIngredient = async (riId) => {
    if (!window.confirm("Remove this ingredient?")) return;
    await fetch(`${API_URL}/api/admin/recipe-ingredients/${riId}`, {
      method: "DELETE", credentials: "include"
    });
    fetchRecipe();
  };

  const saveAmount = async (riId) => {
    await fetch(`${API_URL}/api/admin/recipe-ingredients/${riId}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ amount: parseFloat(editAmount), unit: editUnit }),
    });
    setEditingIngId(null);
    fetchRecipe();
  };

  const saveRecipe = async () => {
    await fetch(`${API_URL}/api/admin/recipes/${id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ name: editName, portions: editPortions, menu_item_id: editMenuItemId || null }),
    });
    setEditingRecipe(false);
    fetchRecipe();
  };

  const saveSessionPrice = async (ingredientId) => {
    await fetch(`${API_URL}/api/admin/sessions/${selectedSession}/prices`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        ingredient_id: ingredientId,
        price_per_unit: parseFloat(editPrice),
        purchase_quantity: parseFloat(editPriceQty),
      }),
    });
    setEditingPriceId(null);
    fetchRecipe();
    fetchSessionPrices();
  };

  if (loading) return (
    <div style={{ padding: "2rem", color: "var(--text-muted)", fontFamily: "'Lato', sans-serif" }}>Loading...</div>
  );
  if (!recipe || recipe.error) return (
    <div style={{ padding: "2rem", color: "var(--text-muted)", fontFamily: "'Lato', sans-serif" }}>Recipe not found.</div>
  );

  return (
    <div style={{ maxWidth: "800px" }}>

      <button onClick={() => navigate("/admin/recipes")} style={backBtnStyle}>
        ← Back to Recipes
      </button>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", margin: "1rem 0 1.5rem" }}>
        {editingRecipe ? (
          <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end", flex: 1, flexWrap: "wrap" }}>
            <div>
              <label style={labelStyle}>Recipe Name</label>
              <input value={editName} onChange={e => setEditName(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Links to Menu Item</label>
              <select value={editMenuItemId} onChange={e => setEditMenuItemId(e.target.value)} style={{ ...inputStyle, minWidth: "160px" }}>
                <option value="">Not linked</option>
                {menu.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Portions</label>
              <input type="number" min="1" value={editPortions}
                onChange={e => setEditPortions(Number(e.target.value))}
                style={{ ...inputStyle, width: "80px" }} />
            </div>
            <button onClick={saveRecipe} style={saveBtnStyle}>Save</button>
            <button onClick={() => setEditingRecipe(false)} style={cancelBtnStyle}>Cancel</button>
          </div>
        ) : (
          <div>
            <h1 className="admin-page-heading" style={{ marginBottom: "0.25rem" }}>{recipe.name}</h1>
            <p style={{ margin: "0.3rem 0 0", color: "var(--text-muted)", fontSize: "0.9rem" }}>
              Makes {recipe.portions} portion{recipe.portions > 1 ? "s" : ""}
            </p>
            {recipe.menu_item_id ? (
              <p style={{ margin: "0.25rem 0 0", fontSize: "0.85rem" }}>
                <span style={{ background: "#EDF5EE", color: "var(--success)", padding: "0.15rem 0.6rem", borderRadius: "999px" }}>
                  🔗 Linked to: {menu.find(m => m.id === recipe.menu_item_id)?.name || "Menu item"}
                </span>
              </p>
            ) : (
              <p style={{ margin: "0.25rem 0 0", fontSize: "0.85rem" }}>
                <span style={{ background: "#FEF4E4", color: "var(--orange)", padding: "0.15rem 0.6rem", borderRadius: "999px" }}>
                  ⚠️ Not linked to a menu item — profit reports won't include this recipe's cost
                </span>
              </p>
            )}
          </div>
        )}
        {!editingRecipe && (
          <button
            onClick={() => { setEditName(recipe.name); setEditPortions(recipe.portions); setEditMenuItemId(recipe.menu_item_id || ""); setEditingRecipe(true); }}
            style={editBtnStyle}
          >
            ✏️ Edit
          </button>
        )}
      </div>

      <div style={{ ...cardStyle, marginBottom: "1.5rem" }}>
        <label style={labelStyle}>
          📅 Price session{" "}
          <span style={{ fontWeight: "normal", color: "var(--text-muted)", textTransform: "none", letterSpacing: "normal" }}>
            — select a session to use that week's ingredient prices
          </span>
        </label>
        <select value={selectedSession} onChange={e => setSelectedSession(e.target.value)}
          style={{ ...inputStyle, maxWidth: "380px", marginTop: "0.25rem" }}>
          <option value="">Use default prices</option>
          {sessions.map(s => (
            <option key={s.id} value={s.id}>
              {new Date(s.open_time).toLocaleString("en-MY", { dateStyle: "medium", timeStyle: "short" })}
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: "flex", gap: "1rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
        <CostCard label="Total Batch Cost" value={`RM ${recipe.total_batch_cost}`} color="var(--orange)" />
        <CostCard label={`Cost per Portion (÷${recipe.portions})`} value={`RM ${recipe.cost_per_portion}`} color="var(--gold)" />
      </div>

      <div style={cardStyle}>
        <h2 style={sectionHeading}>Ingredients</h2>

        {!recipe.ingredients || recipe.ingredients.length === 0 ? (
          <p style={{ color: "var(--text-muted)" }}>No ingredients yet — add one below.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr style={{ background: "var(--beige)", borderBottom: "2px solid var(--border)" }}>
                  <th style={thStyle}>Ingredient</th>
                  <th style={thStyle}>Amount used</th>
                  <th style={thStyle}>Bought as</th>
                  <th style={thStyle}>Price/pkg</th>
                  <th style={thStyle}>Cost</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {recipe.ingredients.map(ing => {
                  const hasOverride = selectedSession && sessionPrices[ing.ingredient_id];
                  return (
                    <tr key={ing.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={tdStyle}>
                        {ing.name}
                        {hasOverride && (
                          <span style={{ marginLeft: "0.4rem", fontSize: "0.72rem", color: "var(--orange)", fontWeight: "bold" }}>
                            session price
                          </span>
                        )}
                      </td>

                      <td style={tdStyle}>
                        {editingIngId === ing.id ? (
                          <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                            <input type="number" min="0.001" step="0.001" value={editAmount}
                              onChange={e => setEditAmount(e.target.value)}
                              style={{ ...inputStyle, width: "75px", marginBottom: 0 }} />
                            <select value={editUnit} onChange={e => setEditUnit(e.target.value)}
                              style={{ ...inputStyle, width: "80px", marginBottom: 0 }}>
                              {RECIPE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                            <button onClick={() => saveAmount(ing.id)} style={saveBtnStyle}>✓</button>
                            <button onClick={() => setEditingIngId(null)} style={cancelBtnStyle}>✕</button>
                          </div>
                        ) : (
                          <span
                            onClick={() => { setEditingIngId(ing.id); setEditAmount(ing.amount); setEditUnit(ing.recipe_unit || "g"); }}
                            style={{ cursor: "pointer", borderBottom: "1px dashed var(--text-muted)" }}
                            title="Click to edit"
                          >
                            {ing.amount} {ing.recipe_unit}
                          </span>
                        )}
                      </td>

                      <td style={{ ...tdStyle, color: "var(--text-muted)", fontSize: "0.85rem" }}>
                        {ing.effective_purchase_qty} {ing.purchase_unit}
                      </td>

                      <td style={tdStyle}>
                        {editingPriceId === ing.ingredient_id ? (
                          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                            <div>
                              <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>Price (RM)</div>
                              <input type="number" step="0.01" value={editPrice}
                                onChange={e => setEditPrice(e.target.value)}
                                style={{ ...inputStyle, width: "70px", marginBottom: 0 }} />
                            </div>
                            <div>
                              <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>Pkg size ({ing.purchase_unit})</div>
                              <input type="number" step="0.001" value={editPriceQty}
                                onChange={e => setEditPriceQty(e.target.value)}
                                style={{ ...inputStyle, width: "70px", marginBottom: 0 }} />
                            </div>
                            <button onClick={() => saveSessionPrice(ing.ingredient_id)}
                              style={{ ...saveBtnStyle, alignSelf: "flex-end" }}>✓</button>
                            <button onClick={() => setEditingPriceId(null)}
                              style={{ ...cancelBtnStyle, alignSelf: "flex-end" }}>✕</button>
                          </div>
                        ) : (
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <span>RM {parseFloat(ing.effective_price).toFixed(2)}</span>
                            {selectedSession && (
                              <button
                                onClick={() => {
                                  setEditingPriceId(ing.ingredient_id);
                                  setEditPrice(ing.effective_price);
                                  setEditPriceQty(ing.effective_purchase_qty);
                                }}
                                style={{ ...editBtnStyle, fontSize: "0.72rem", padding: "0.15rem 0.45rem" }}
                                title="Override price for this session"
                              >
                                ✏️
                              </button>
                            )}
                          </div>
                        )}
                      </td>

                      <td style={{ ...tdStyle, fontWeight: "bold", color: "var(--gold)" }}>
                        RM {parseFloat(ing.cost).toFixed(4)}
                        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontWeight: "normal", marginTop: "0.15rem" }}>
                          {ing.conversion_used}
                        </div>
                      </td>

                      <td style={tdStyle}>
                        <button onClick={() => removeIngredient(ing.id)} style={deleteBtnStyle}>🗑</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ borderTop: "1px solid var(--border)", paddingTop: "1rem", marginTop: "0.5rem" }}>
          <h3 style={{ margin: "0 0 0.75rem", fontSize: "0.95rem", fontFamily: "'Playfair Display', Georgia, serif", color: "var(--text-main)" }}>+ Add Ingredient</h3>
          <form onSubmit={addIngredient} style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ flex: 3, minWidth: "180px" }}>
              <label style={labelStyle}>Ingredient</label>
              <select value={addIngId} onChange={e => setAddIngId(e.target.value)} style={inputStyle}>
                <option value="">Select ingredient...</option>
                {allIngredients.map(i => (
                  <option key={i.id} value={i.id}>
                    {i.name} ({i.purchase_quantity} {i.unit} — RM {parseFloat(i.price_per_unit).toFixed(2)})
                  </option>
                ))}
              </select>
            </div>
            <div style={{ minWidth: "90px" }}>
              <label style={labelStyle}>Amount</label>
              <input type="number" min="0.001" step="0.001"
                value={addAmount} onChange={e => setAddAmount(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ minWidth: "90px" }}>
              <label style={labelStyle}>Unit</label>
              <select value={addUnit} onChange={e => setAddUnit(e.target.value)} style={inputStyle}>
                {RECIPE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <button type="submit" style={saveBtnStyle}>Add</button>
          </form>
          {addError && <p style={errorStyle}>{addError}</p>}
        </div>
      </div>
    </div>
  );
}

function CostCard({ label, value, color }) {
  return (
    <div style={{
      background: "var(--warm-white)", border: "1px solid var(--border)", borderRadius: "14px",
      padding: "1rem 1.25rem", borderTop: `4px solid ${color}`,
      minWidth: "160px", flex: 1, boxShadow: "0 4px 24px rgba(92, 51, 23, 0.08)",
    }}>
      <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--brown-mid)", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.04em", fontFamily: "'Lato', sans-serif" }}>{label}</p>
      <p style={{ margin: "0.4rem 0 0", fontSize: "1.3rem", fontWeight: "bold", color: "var(--text-main)", fontFamily: "'Playfair Display', Georgia, serif" }}>{value}</p>
    </div>
  );
}

const sectionHeading = { marginTop: 0, fontSize: "1.1rem", fontFamily: "'Playfair Display', Georgia, serif", color: "var(--text-main)" };
const cardStyle = { background: "var(--warm-white)", border: "1px solid var(--border)", borderRadius: "16px", padding: "1.5rem", boxShadow: "0 4px 24px rgba(92, 51, 23, 0.08)" };
const tableStyle = { width: "100%", borderCollapse: "collapse" };
const labelStyle = { display: "block", fontWeight: "600", fontSize: "0.82rem", color: "var(--brown-mid)", marginBottom: "0.3rem", textTransform: "uppercase", letterSpacing: "0.04em" };
const inputStyle = { display: "block", width: "100%", padding: "0.55rem 0.7rem", marginBottom: "0", borderRadius: "10px", border: "1.5px solid var(--border)", background: "var(--beige)", fontSize: "0.95rem", boxSizing: "border-box", color: "var(--text-main)" };
const saveBtnStyle = { background: "var(--orange)", color: "white", border: "none", padding: "0.45rem 1rem", borderRadius: "8px", fontSize: "0.88rem", cursor: "pointer", fontWeight: "700" };
const cancelBtnStyle = { background: "transparent", border: "1px solid var(--border)", padding: "0.45rem 0.75rem", borderRadius: "8px", fontSize: "0.88rem", cursor: "pointer", color: "var(--text-muted)" };
const editBtnStyle = { background: "var(--beige)", border: "1px solid var(--border)", borderRadius: "8px", padding: "0.3rem 0.7rem", fontSize: "0.85rem", cursor: "pointer" };
const deleteBtnStyle = { background: "#FDECEC", border: "1px solid #E8A8A8", borderRadius: "8px", padding: "0.3rem 0.6rem", fontSize: "0.82rem", cursor: "pointer", color: "var(--error)" };
const backBtnStyle = { background: "transparent", border: "none", color: "var(--orange)", cursor: "pointer", fontSize: "0.9rem", padding: 0, fontWeight: "600" };
const thStyle = { padding: "0.6rem 0.75rem", textAlign: "left", fontSize: "0.78rem", fontWeight: "700", color: "var(--brown-mid)", textTransform: "uppercase", letterSpacing: "0.04em" };
const tdStyle = { padding: "0.65rem 0.75rem", fontSize: "0.9rem", color: "var(--text-main)", verticalAlign: "middle" };
const errorStyle = { color: "var(--error)", fontSize: "0.88rem", marginTop: "0.5rem" };

export default RecipeDetail;